import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { SCConfig } from '../../sc-config/constant/sc-config.constant';
import { DatatableModel } from '../standalone-packages/ecap3/app-platform-datatable/models/datatable-model.model';
import { InsuranceCompanySeed } from '../models/domain/InsuranceCompanySeed';
import { PCXConstants } from '../../sc-config/constant/PCXConstants';
import { Filter } from '../../shared/models';
import { GqlQueryBuilderService } from './gql-query-builder.service';
import { StoreDataService } from './store-data.service';
import { DefaultsService } from '../../../app/app-master-data/service/defaults.service';
import { Trade } from '../../../app/shared/models/service-provider-info-model';
import { UrlResolverService } from './url/url-resolver.service';
@Injectable({
    providedIn: 'root'
})
export class ServiceProviderService {
    private readonly urlResolver = inject(UrlResolverService);
    private readonly pcxQueryUrl: string = this.urlResolver.getPcxQueryUrl();
    private readonly externalIpexUrl: string = this.urlResolver.getExternalIpexUrl();
    datatableModel = new DatatableModel<InsuranceCompanySeed>('CreateDate', 'desc');

    private readonly languageList = [
        { id: 'en', value: 'en-US' },
        { id: 'de', value: 'de-DE' },
        { id: 'cz', value: 'cs-CZ' },
        { id: 'fr', value: 'fr-FR' },
        { id: 'it', value: 'it-IT' }
    ];
    constructor(
        private readonly httpClient: HttpClient,
        private _service: DefaultsService,
        private gqlQueryBuilderService: GqlQueryBuilderService
    ) {}

    public getServiceProviderCount(serviceProviderIds: string[]): Observable<any> {
        let url = this.externalIpexUrl + 'CommunicateIpexMicroservice';
        const providerPayload = {
            serviceProviderIds
        };
        const payload = {
            Payload: JSON.stringify(providerPayload),
            TargetEndpoint: 'api/v1/connected-cases/connected-to-service-provider',
            HttpMethod: 'POST',
            CredentialGroup: 'ServiceProviderMSConfiguration'
        };
        return this.httpClient.post<any>(url, payload, {
            headers: SCConfig.HttpCommonHeader,
            withCredentials: true
        });
    }

    public getServiceProviderDetail(serviceProviderItemId: string): Observable<any> {
        let url = this.externalIpexUrl + 'CommunicateIpexMicroservice';
        const payload = {
            Payload: '',
            TargetEndpoint: `api/v1/service-provider/details-with-cases/${serviceProviderItemId}`,
            HttpMethod: 'GET',
            CredentialGroup: 'ServiceProviderMSConfiguration'
        };
        return this.httpClient.post<any>(url, payload, {
            headers: SCConfig.HttpCommonHeader,
            withCredentials: true
        });
    }

    public getAllTrade(language: string): Observable<any> {
        const languageKey = this.convertLanguageKeyToIpexWorld(language);
        let url = this.externalIpexUrl + 'CommunicateIpexMicroservice';
        const payload = {
            Payload: '',
            TargetEndpoint: `databeast/v1/tradeService/allTrades?locale=${languageKey}`,
            HttpMethod: 'GET',
            CredentialGroup: 'IpexTradeConfiguration'
        };
        return this.httpClient.post<any>(url, payload, {
            headers: SCConfig.HttpCommonHeader,
            withCredentials: true
        });
    }

    public convertLanguageKeyToIpexWorld(language: string): string {
        if (['en-US', 'en', 'en_US'].includes(language)) return 'en_US';
        if (['fr-FR', 'fr', 'fr_CH'].includes(language)) return 'fr_CH';
        if (['it-IT', 'it', 'it_CH'].includes(language)) return 'it_CH';
        return 'de_CH';
    }

    public getServiceProviderTooltipPosition(event: any, tooltip: any): string {
        const parentRect = (event.target as HTMLElement).getBoundingClientRect();
        const tooltipRect = tooltip.getBoundingClientRect();

        const spaceAbove = parentRect.top - 45;
        const spaceBelow = window.innerHeight - parentRect.bottom;

        // setting tooltip height
        // while using display none, DOM is not ready yet
        // and tooltipRect.height does not know the actual height
        tooltipRect.height = 435;

        let position = null;
        // Check for available space and position the tooltip
        if (spaceBelow >= tooltipRect.height) {
            position = `${parentRect.height + 25}px`;
        } else if (spaceAbove >= tooltipRect.height) {
            position = `-${tooltipRect.height + 5}px`;
        } else {
            const centerOffset = (parentRect.height - tooltipRect.height) / 2;
            position = `${centerOffset}px`;
        }

        return position;
    }

    prepareAllTrade(data: any, languageKey: string): Trade[] {
        let tradeList: Trade[] = [];
        languageKey = this.languageList.find(l => l.id === languageKey)?.value ?? languageKey;
        if (!data || data.length === 0) {
            return [];
        }
        let response = StoreDataService.ParseJsonStringToJson(data);

        if (!response || response.length === 0) {
            return [];
        }

        response.forEach(item => {
            const label = item.Label?.find((l: any) => l.Key === languageKey);
            if (label) {
                tradeList.push({
                    id: item.ItemId,
                    key: item.Key,
                    name: label?.Value ?? ''
                } as Trade);
            }
        });
        return tradeList.sort((a, b) => a.name.localeCompare(b.name));
    }

    getAllTradeFromCommonSeedData(pageSize: number): Observable<any> {
        this.datatableModel.PageSize = pageSize;
        const filters: Filter[] = [new Filter('EntityName', '=', PCXConstants.SeedEntityName.TradeSeed)];
        const filterString = `{${this.gqlQueryBuilderService.prepareFilterString(filters)}}`;
        return this._service.getGenericEntityData('SeedMetaDatas', this.datatableModel, filterString);
    }
}
